import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Chip,
  Stack,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useVisitas } from '../hooks/useVisitas';
import { VisitaDetailDrawer } from '../components/VisitaDetailDrawer';
import type { Visita } from '../hooks/useVisitas';
import { useAuth } from '../../../hooks/useAuth';

// AIDEV-NOTE: Mapeamento de status para cor de Chip
const STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'error'> = {
  em_andamento: 'info',
  concluida: 'success',
  cancelada: 'error'
};

const TIPO_OPTIONS = [
  { value: '', label: 'Todos os tipos' },
  { value: 'prospeccao', label: 'Prospecao' },
  { value: 'manutencao', label: 'Manutencao' },
  { value: 'cobranca', label: 'Cobranca' },
  { value: 'entrega', label: 'Entrega' },
  { value: 'outro', label: 'Outro' }
] as const;

function formatDuration(minutes?: number): string {
  if (!minutes) return '--';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0) return `${h}h ${m}min`;
  return `${m}min`;
}

export function HistoricoVisitasPage() {
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [tipoVisita, setTipoVisita] = useState('');
  const [vendedorFiltro, setVendedorFiltro] = useState('');
  const [drawerVisita, setDrawerVisita] = useState<Visita | null>(null);

  // AIDEV-NOTE: Admin/gerente veem visitas de todos os vendedores (server sem filtro de vendedor)
  const { user } = useAuth();
  const isAdminOuGerente = user?.perfil === 'admin' || user?.perfil === 'gerente';

  // Monta os parametros de query baseado nos filtros ativos
  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (dataInicio) p.data_inicio = dataInicio;
    if (dataFim) p.data_fim = dataFim;
    if (tipoVisita) p.tipo_visita = tipoVisita;
    return p;
  }, [dataInicio, dataFim, tipoVisita]);

  const { data: visitas = [], isLoading } = useVisitas(params);

  // Opcoes do filtro de vendedor derivadas dos dados carregados
  const vendedorOptions = useMemo(() => {
    const nomes = new Set<string>();
    for (const v of visitas as Visita[]) {
      if (v.vendedor_nome) nomes.add(v.vendedor_nome);
    }
    return [...nomes].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [visitas]);

  // Ordenar cronologica reversa + filtro local por vendedor (admin/gerente)
  const sortedVisitas = useMemo(() => {
    const filtradas = vendedorFiltro
      ? (visitas as Visita[]).filter((v) => v.vendedor_nome === vendedorFiltro)
      : (visitas as Visita[]);
    return [...filtradas].sort(
      (a, b) => new Date(b.checkin_at).getTime() - new Date(a.checkin_at).getTime()
    );
  }, [visitas, vendedorFiltro]);

  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight={600} mb={3}>
        Historico de Visitas
      </Typography>

      {/* Filtros */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <TextField
            type="date"
            label="Data inicio"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          <TextField
            type="date"
            label="Data fim"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Tipo de Visita</InputLabel>
            <Select
              value={tipoVisita}
              label="Tipo de Visita"
              onChange={(e) => setTipoVisita(e.target.value)}
            >
              {TIPO_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isAdminOuGerente && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Vendedor</InputLabel>
              <Select
                value={vendedorFiltro}
                label="Vendedor"
                onChange={(e) => setVendedorFiltro(e.target.value)}
              >
                <MenuItem value="">Todos os vendedores</MenuItem>
                {vendedorOptions.map((nome) => (
                  <MenuItem key={nome} value={nome}>
                    {nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </Paper>

      {/* Tabela de visitas */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={24} />
        </Box>
      ) : sortedVisitas.length === 0 ? (
        <Box textAlign="center" py={6}>
          <Typography variant="body1" color="text.secondary">
            Nenhuma visita encontrada com os filtros selecionados.
          </Typography>
        </Box>
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell>Cliente</TableCell>
                {isAdminOuGerente && <TableCell>Vendedor</TableCell>}
                <TableCell>Data</TableCell>
                <TableCell>Duracao</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Acoes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedVisitas.map((visita) => (
                <TableRow
                  key={visita.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setDrawerVisita(visita)}
                >
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {visita.cliente_nome ?? `Cliente ${visita.cliente_id}`}
                    </Typography>
                  </TableCell>
                  {isAdminOuGerente && (
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                        {visita.vendedor_nome ?? visita.vendedor_id}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(visita.checkin_at).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(visita.checkin_at).toLocaleTimeString('pt-BR', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDuration(visita.duracao_minutos)}</TableCell>
                  <TableCell>
                    {visita.tipo_visita ? (
                      <Chip
                        label={visita.tipo_visita}
                        size="small"
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.75rem' }}
                      />
                    ) : (
                      '--'
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={visita.status}
                      size="small"
                      color={STATUS_COLOR[visita.status] ?? 'default'}
                      sx={{ height: 22, fontSize: '0.75rem' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDrawerVisita(visita);
                      }}
                    >
                      Ver
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Drawer de detalhes */}
      <VisitaDetailDrawer
        open={!!drawerVisita}
        onClose={() => setDrawerVisita(null)}
        visita={drawerVisita}
      />
    </Box>
  );
}
